/**
 * Individual Policy Page - Dynamic content based on policy type
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';

function PolicyPage() {
  const { policyId } = useParams();

  const policyContent = {
    terms: {
      title: 'Terms of Service',
      content: `
# Terms of Service

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Agreement to Terms

By accessing or using HotBump ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.

## 2. Description of Service

HotBump is an AI-powered platform that allows users to create, interact with, and share AI-generated characters and content. The Service includes but is not limited to:
- Character creation and customization
- AI-powered chat interactions
- Media generation (images and videos)
- Social features (likes, comments, sharing)

## 3. User Accounts

### 3.1 Account Creation
- You must be at least 18 years old to use this Service
- You are responsible for maintaining the confidentiality of your account credentials
- You agree to provide accurate and complete information when creating an account

### 3.2 Account Security
- You are responsible for all activities that occur under your account
- You must immediately notify us of any unauthorized use of your account

## 4. User Conduct

You agree not to:
- Violate any applicable laws or regulations
- Infringe upon the rights of others
- Upload or share prohibited content (see Prohibited Content Policy)
- Attempt to gain unauthorized access to the Service
- Interfere with or disrupt the Service or servers

## 5. Content Ownership and Licensing

### 5.1 User-Generated Content
- You retain ownership of content you create using the Service
- By using the Service, you grant HotBump a license to use, display, and distribute your content as necessary to provide the Service

### 5.2 AI-Generated Content
- AI-generated content created using our Service is subject to our Content Moderation Policy
- HotBump reserves the right to moderate, remove, or restrict access to any content

## 6. Payment and Billing

### 6.1 Token System
- The Service operates on a token-based payment system
- Tokens are non-refundable except as specified in our Refund Policy
- Token prices and availability are subject to change

### 6.2 Payment Methods
- We accept cryptocurrency payments through Plisio
- All payments are processed securely through third-party payment processors

## 7. Prohibited Activities

The following activities are strictly prohibited:
- Creating content that violates our Prohibited Content Policy
- Attempting to circumvent content moderation
- Using the Service for illegal purposes
- Harassing, threatening, or harming other users

## 8. Intellectual Property

- The Service and its original content, features, and functionality are owned by HotBump
- Our trademarks, service marks, and logos are proprietary to HotBump

## 9. Termination

We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.

## 10. Disclaimer of Warranties

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.

## 11. Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, HOTBUMP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES.

## 12. Changes to Terms

We reserve the right to modify these Terms at any time. We will notify users of any material changes via email or through the Service.

## 13. Contact Information

For questions about these Terms, please contact us at:
- Email: support@hotbump.online
- Website: https://hotbump.online
      `
    },
    privacy: {
      title: 'Privacy Policy',
      content: `
# Privacy Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Information We Collect

### 1.1 Information You Provide
- Account information (email address, username)
- Payment information (processed through secure third-party processors)
- Content you create (characters, messages, media)

### 1.2 Automatically Collected Information
- Usage data and analytics
- Device information
- IP address and location data
- Cookies and similar tracking technologies

## 2. How We Use Your Information

We use collected information to:
- Provide, maintain, and improve the Service
- Process payments and transactions
- Send you technical notices and support messages
- Respond to your comments and questions
- Monitor and analyze usage patterns
- Detect, prevent, and address technical issues

## 3. Information Sharing and Disclosure

We do not sell your personal information. We may share information:
- With service providers who assist in operating our Service
- When required by law or to protect rights and safety
- In connection with a business transfer or merger

## 4. Data Security

We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.

## 5. Your Rights

Depending on your location, you may have the right to:
- Access your personal information
- Correct inaccurate data
- Request deletion of your data
- Object to processing of your data
- Data portability

## 6. Cookies and Tracking

We use cookies and similar technologies to:
- Remember your preferences
- Analyze Service usage
- Provide personalized content

## 7. Third-Party Services

Our Service may contain links to third-party websites or services. We are not responsible for their privacy practices.

## 8. Children's Privacy

Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.

## 9. International Data Transfers

Your information may be transferred to and processed in countries other than your country of residence.

## 10. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page.

## 11. Contact Us

For privacy-related questions, contact us at:
- Email: support@hotbump.online
- Website: https://hotbump.online
      `
    },
    refund: {
      title: 'Refund Policy',
      content: `
# Refund Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. General Policy

HotBump operates on a token-based payment system. Tokens are generally non-refundable once purchased and used. However, we understand that exceptional circumstances may arise.

## 2. Token Purchases

### 2.1 Non-Refundable Tokens
- Tokens are digital goods and are generally non-refundable
- Once tokens are credited to your account, they cannot be refunded
- Used tokens cannot be refunded under any circumstances

### 2.2 Exceptions
We may consider refunds in the following cases:
- Duplicate payments due to technical errors
- Unauthorized transactions (subject to verification)
- Service unavailability preventing token usage (at our discretion)

## 3. Subscription Refunds

If we introduce subscription services in the future:
- Monthly subscriptions may be cancelled at any time
- Refunds for unused portions of subscriptions are at our discretion
- Annual subscriptions may have specific refund terms outlined at purchase

## 4. Requesting a Refund

To request a refund:
1. Contact support@hotbump.online within 7 days of the transaction
2. Provide transaction details and reason for refund
3. Allow up to 14 business days for review and processing

## 5. Processing Refunds

- Approved refunds will be processed to the original payment method
- Cryptocurrency refunds will be processed to the original wallet address
- Processing time may vary based on payment method

## 6. Disputes

If you are not satisfied with our refund decision, you may:
- Contact us for further review
- Dispute the transaction through your payment provider (subject to their policies)

## 7. Contact

For refund requests or questions:
- Email: support@hotbump.online
- Response time: Within 24-48 hours
      `
    },
    prohibited: {
      title: 'Prohibited Content Policy',
      content: `
# Prohibited Content Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

HotBump is committed to maintaining a safe and legal platform. This policy outlines content that is strictly prohibited on our Service.

## 2. Prohibited Content Categories

### 2.1 Illegal Content
- Content that violates any applicable laws or regulations
- Content promoting illegal activities
- Content that infringes on intellectual property rights

### 2.2 Harmful Content
- Content that promotes violence, self-harm, or suicide
- Content that incites hatred or discrimination
- Content that threatens or harasses individuals

### 2.3 Underage Content
- Any content depicting or suggesting individuals under 18 years of age
- Content that appears to sexualize minors
- See our Underage Policy for detailed information

### 2.4 Non-Consensual Content
- Content depicting non-consensual sexual acts
- Revenge porn or similar content
- Content that violates privacy or consent

### 2.5 Extreme Content
- Extremely graphic violence
- Content that promotes terrorism or extremism
- Content that glorifies abuse or exploitation

## 3. Content Moderation

### 3.1 Pre-Screening
- All user-created characters undergo moderation before becoming public
- Content is reviewed for compliance with this policy

### 3.2 Post-Screening
- We continuously monitor content on our platform
- Users may report content that violates this policy

## 4. Consequences of Violations

Violations of this policy may result in:
- Content removal
- Account suspension or termination
- Legal action if applicable
- Reporting to relevant authorities

## 5. Reporting Violations

To report prohibited content:
- Use the reporting feature within the Service
- Email: support@hotbump.online
- Include content details and reason for report

## 6. Appeals Process

If you believe content was incorrectly removed:
- Contact support@hotbump.online
- Provide details and justification
- Allow up to 7 business days for review

## 7. Contact

For questions about this policy:
- Email: support@hotbump.online
      `
    },
    removal: {
      title: 'Content Removal Policy',
      content: `
# Content Removal Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

This policy explains when and how content may be removed from HotBump.

## 2. Grounds for Removal

Content may be removed if it:
- Violates our Terms of Service
- Violates our Prohibited Content Policy
- Infringes on intellectual property rights
- Violates applicable laws or regulations
- Is reported by users and found to be in violation
- Is determined to be harmful, abusive, or inappropriate

## 3. Removal Process

### 3.1 Automatic Removal
- Content that clearly violates our policies may be removed immediately
- Automated systems may flag and remove certain types of content

### 3.2 Review-Based Removal
- Reported content is reviewed by our moderation team
- Removal decisions are made within 24-48 hours of report

### 3.3 Legal Removal
- Content may be removed in response to valid legal requests
- We comply with applicable laws and court orders

## 4. Notification of Removal

When content is removed:
- Users are notified via email or in-app notification
- Reason for removal is provided when appropriate
- Users may appeal removal decisions

## 5. User-Initiated Removal

Users may:
- Delete their own content at any time
- Request removal of content they created
- Report content created by others

## 6. Appeals Process

If you believe content was incorrectly removed:
1. Contact support@hotbump.online within 7 days
2. Provide content details and justification
3. Allow up to 7 business days for review
4. We will notify you of the decision

## 7. Permanent Removal

Some content may be permanently removed and cannot be restored:
- Content that violates legal requirements
- Content that poses safety risks
- Content removed due to legal obligations

## 8. Contact

For content removal questions:
- Email: support@hotbump.online
      `
    },
    moderation: {
      title: 'Content Moderation Policy',
      content: `
# Content Moderation Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

HotBump employs a comprehensive content moderation system to ensure a safe and compliant platform.

## 2. Moderation Approach

### 2.1 Pre-Screening
- All user-created characters are reviewed before becoming public
- Content is checked against our Prohibited Content Policy
- Moderation typically occurs within 24-48 hours

### 2.2 Post-Screening
- We continuously monitor content on our platform
- Automated systems and human moderators review content
- User reports are investigated promptly

## 3. Moderation Criteria

Content is evaluated based on:
- Compliance with Terms of Service
- Adherence to Prohibited Content Policy
- Legal requirements
- Community standards
- User reports and feedback

## 4. Moderation Actions

Based on review, we may:
- Approve content for publication
- Request content modifications
- Reject content with explanation
- Remove published content
- Suspend or terminate accounts

## 5. Automated Moderation

We use automated systems to:
- Detect prohibited content patterns
- Flag content for human review
- Prevent spam and abuse
- Monitor system health

## 6. Human Moderation

Human moderators:
- Review flagged content
- Make final decisions on edge cases
- Handle user appeals
- Ensure consistent policy application

## 7. Transparency

We strive to:
- Provide clear reasons for moderation decisions
- Allow users to appeal decisions
- Maintain consistent standards
- Communicate policy changes

## 8. User Responsibilities

Users are expected to:
- Review and comply with our policies
- Report content that violates policies
- Respect moderation decisions
- Create content in good faith

## 9. Appeals

If you disagree with a moderation decision:
- Contact support@hotbump.online
- Provide details and justification
- Allow up to 7 business days for review

## 10. Contact

For moderation questions:
- Email: support@hotbump.online
      `
    },
    screening: {
      title: 'Pre-Screening and Post-Screening Policy',
      content: `
# Pre-Screening and Post-Screening Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

This policy outlines our content review processes, including pre-screening (before publication) and post-screening (after publication).

## 2. Pre-Screening Process

### 2.1 When Pre-Screening Occurs
- All user-created characters are pre-screened before becoming public
- Content is reviewed for compliance with our policies
- Pre-screening typically occurs within 24-48 hours

### 2.2 Pre-Screening Criteria
Content is evaluated for:
- Compliance with Terms of Service
- Adherence to Prohibited Content Policy
- Legal compliance
- Platform appropriateness

### 2.3 Pre-Screening Outcomes
- **Approved**: Content is made public
- **Rejected**: Content is not published, user receives explanation
- **Modification Requested**: User may modify and resubmit

## 3. Post-Screening Process

### 3.1 Continuous Monitoring
- Published content is continuously monitored
- Automated systems flag potentially problematic content
- User reports trigger immediate review

### 3.2 Post-Screening Actions
If violations are found:
- Content may be removed immediately
- Users are notified of removal and reason
- Accounts may be subject to disciplinary action

## 4. Review Timeline

- **Pre-screening**: 24-48 hours
- **Post-screening review**: Within 24 hours of report
- **Appeals**: Up to 7 business days

## 5. User Notification

Users are notified:
- When content is approved or rejected
- When content is removed post-publication
- When modifications are requested
- When appeals are processed

## 6. Appeals Process

If you disagree with a screening decision:
1. Contact support@hotbump.online
2. Provide content details and justification
3. Allow up to 7 business days for review
4. We will notify you of the final decision

## 7. Transparency

We are committed to:
- Clear communication of decisions
- Fair and consistent application of policies
- Timely responses to user inquiries
- Continuous improvement of our processes

## 8. Contact

For screening questions:
- Email: support@hotbump.online
      `
    },
    complaint: {
      title: 'Complaint Policy',
      content: `
# Complaint Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

HotBump is committed to addressing user complaints fairly and promptly. This policy outlines our complaint handling process.

## 2. Types of Complaints

We accept complaints regarding:
- Service quality or functionality
- Content moderation decisions
- Account actions (suspensions, terminations)
- Payment or billing issues
- Privacy concerns
- Policy violations by other users

## 3. How to File a Complaint

### 3.1 Email Submission
- Send detailed complaint to: support@hotbump.online
- Include: your account information, description of issue, relevant evidence
- Subject line: "Complaint: [Brief Description]"

### 3.2 In-App Reporting
- Use reporting features within the Service
- Provide as much detail as possible
- Include screenshots or evidence when applicable

## 4. Complaint Processing

### 4.1 Initial Response
- We acknowledge receipt within 24-48 hours
- Complaints are assigned a reference number

### 4.2 Investigation
- Complaints are reviewed by appropriate team members
- Investigation typically takes 5-7 business days
- Complex cases may take longer

### 4.3 Resolution
- We provide a written response with our findings
- If complaint is upheld, we take appropriate corrective action
- If complaint is not upheld, we provide explanation

## 5. Response Timeline

- **Acknowledgment**: 24-48 hours
- **Initial Response**: 5-7 business days
- **Complex Cases**: Up to 14 business days

## 6. Escalation

If you are not satisfied with the initial response:
- Request escalation to a supervisor
- Provide additional information or clarification
- Allow additional time for review

## 7. Confidentiality

We treat all complaints confidentially:
- Information is shared only with necessary personnel
- We respect privacy and data protection requirements
- Complaints are stored securely

## 8. Good Faith Complaints

We encourage good faith complaints:
- False or malicious complaints may result in account action
- We take abuse of the complaint system seriously

## 9. Contact

For complaints or questions:
- Email: support@hotbump.online
- Response time: Within 24-48 hours
      `
    },
    '2257': {
      title: '18 U.S.C 2257 Exemption',
      content: `
# 18 U.S.C 2257 Exemption

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Exemption Notice

HotBump operates as a platform for AI-generated content. All content on our platform is generated using artificial intelligence and does not depict actual human beings.

## 2. AI-Generated Content

All characters, images, and media on HotBump are:
- Generated using artificial intelligence technology
- Not depictions of real individuals
- Created through algorithmic processes
- Not subject to 18 U.S.C. 2257 record-keeping requirements

## 3. No Human Models

HotBump does not:
- Employ human models or performers
- Produce content featuring real individuals
- Maintain records of human performers
- Operate as a traditional adult content producer

## 4. User-Generated Content

When users create content using our AI tools:
- The content is AI-generated, not photographic
- No human models are involved in the creation process
- Content is subject to our Content Moderation Policy

## 5. Legal Compliance

We comply with all applicable laws and regulations:
- We maintain appropriate content policies
- We prohibit content that violates laws
- We cooperate with legal authorities when required

## 6. Verification

All content on our platform is:
- Generated through AI processes
- Verified as AI-generated through our systems
- Subject to our moderation policies

## 7. Contact

For questions about this exemption:
- Email: support@hotbump.online
- Legal inquiries: legal@hotbump.online
      `
    },
    underage: {
      title: 'Underage Policy',
      content: `
# Underage Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Zero Tolerance

HotBump has a strict zero-tolerance policy regarding content that depicts, suggests, or sexualizes individuals who appear to be under 18 years of age.

## 2. Age Requirement

### 2.1 Service Access
- Users must be at least 18 years old to use HotBump
- We verify age during account creation
- False age information results in immediate account termination

### 2.2 Content Creation
- All characters must be explicitly 18 years or older
- Content suggesting underage individuals is strictly prohibited
- Age verification is required for character creation

## 3. Prohibited Content

The following is strictly prohibited:
- Content depicting or suggesting individuals under 18
- Content that sexualizes minors or appears to be minors
- Character descriptions indicating age under 18
- Any content that could be interpreted as involving minors

## 4. Content Moderation

### 4.1 Pre-Screening
- All characters are reviewed for age compliance
- Content suggesting underage individuals is rejected
- Age verification is part of our moderation process

### 4.2 Post-Screening
- We continuously monitor for age-related violations
- User reports are investigated immediately
- Violations result in immediate content removal

## 5. Enforcement

Violations of this policy result in:
- Immediate content removal
- Permanent account termination
- Reporting to relevant authorities
- Legal action when applicable

## 6. Reporting

If you encounter content that violates this policy:
- Report immediately through the Service
- Email: support@hotbump.online
- Include content details and evidence

## 7. AI-Generated Content

Even though our content is AI-generated:
- We maintain strict age policies
- Content must clearly depict adults (18+)
- Age verification is required for all characters

## 8. Legal Compliance

We comply with:
- All applicable laws regarding minor protection
- International child protection standards
- Reporting requirements for suspected violations

## 9. Contact

For questions or reports:
- Email: support@hotbump.online
- Emergency: Report immediately through the Service
      `
    },
    aml: {
      title: 'AML/Anti-Fraud Policy',
      content: `
# AML/Anti-Fraud Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Overview

HotBump is committed to preventing money laundering, fraud, and other financial crimes. This policy outlines our anti-money laundering (AML) and anti-fraud measures.

## 2. Payment Security

### 2.1 Payment Processing
- All payments are processed through secure, third-party payment processors
- We use Plisio for cryptocurrency payments
- Payment information is encrypted and secured

### 2.2 Transaction Monitoring
- We monitor transactions for suspicious activity
- Unusual patterns trigger additional verification
- Large transactions may require additional verification

## 3. Fraud Prevention

### 3.1 Account Security
- We implement security measures to protect accounts
- Suspicious login activity is monitored
- Multi-factor authentication may be required

### 3.2 Transaction Verification
- We verify payment methods and accounts
- Suspicious transactions are flagged for review
- Fraudulent transactions are reported to authorities

## 4. User Verification

We may require:
- Identity verification for certain transactions
- Additional documentation for large purchases
- Verification of payment method ownership

## 5. Suspicious Activity Reporting

If we detect suspicious activity:
- We may suspend accounts pending investigation
- We report to relevant authorities when required
- We cooperate with law enforcement investigations

## 6. Prohibited Activities

The following activities are prohibited:
- Money laundering
- Fraudulent transactions
- Identity theft
- Payment method fraud
- Chargeback fraud

## 7. Compliance

We comply with:
- Applicable AML regulations
- Anti-fraud best practices
- Financial crime prevention standards
- Reporting requirements

## 8. User Responsibilities

Users must:
- Provide accurate payment information
- Not engage in fraudulent activities
- Report suspicious activity
- Comply with applicable laws

## 9. Consequences

Violations may result in:
- Account suspension or termination
- Transaction reversal
- Legal action
- Reporting to authorities

## 10. Contact

For fraud-related concerns:
- Email: support@hotbump.online
- Security: security@hotbump.online
      `
    },
    trafficking: {
      title: 'Sex Trafficking Policy',
      content: `
# Sex Trafficking Policy

**Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}**

## 1. Zero Tolerance

HotBump has a strict zero-tolerance policy regarding sex trafficking, human trafficking, and any form of exploitation.

## 2. Prohibited Content and Activities

The following are strictly prohibited:
- Content that promotes or facilitates trafficking
- Content that depicts non-consensual situations
- Content that exploits vulnerable individuals
- Any activity related to human trafficking

## 3. Content Moderation

### 3.1 Pre-Screening
- All content is reviewed for trafficking-related violations
- Content suggesting exploitation is immediately rejected
- We use automated and human moderation

### 3.2 Post-Screening
- We continuously monitor for trafficking-related content
- User reports are investigated immediately
- Violations result in immediate action

## 4. Reporting Requirements

If we identify potential trafficking:
- We report to relevant authorities immediately
- We preserve evidence as required by law
- We cooperate fully with investigations

## 5. User Reporting

Users must report:
- Any content suggesting trafficking
- Suspicious user behavior
- Any indication of exploitation
- Contact information: support@hotbump.online

## 6. Legal Compliance

We comply with:
- All applicable anti-trafficking laws
- International trafficking prevention standards
- Law enforcement cooperation requirements
- Reporting obligations

## 7. Enforcement

Violations result in:
- Immediate content removal
- Permanent account termination
- Immediate reporting to authorities
- Full cooperation with law enforcement

## 8. AI-Generated Content

Even though our content is AI-generated:
- We prohibit content that suggests trafficking
- We maintain strict moderation standards
- We report suspicious patterns

## 9. Training and Awareness

Our team:
- Receives training on trafficking indicators
- Stays updated on legal requirements
- Maintains awareness of best practices

## 10. Contact

For reports or questions:
- Email: support@hotbump.online
- Emergency: Report immediately through the Service
- Law Enforcement: legal@hotbump.online
      `
    }
  };

  const policy = policyContent[policyId];

  if (!policy) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-dark-900 to-burgundy-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">Policy Not Found</h1>
          <Link to="/web/policies" className="text-burgundy-400 hover:text-burgundy-300">
            ← Back to Policies
          </Link>
        </div>
      </div>
    );
  }

  // Simple markdown-like rendering
  const renderContent = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentList = [];
    let inList = false;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('# ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(<h1 key={index} className="text-3xl font-bold mb-6">{trimmed.substring(2)}</h1>);
      } else if (trimmed.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(<h2 key={index} className="text-2xl font-semibold mt-6 mb-4 text-burgundy-400">{trimmed.substring(3)}</h2>);
      } else if (trimmed.startsWith('### ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(<h3 key={index} className="text-xl font-medium mt-4 mb-2">{trimmed.substring(4)}</h3>);
      } else if (trimmed.startsWith('- ')) {
        if (!inList) {
          inList = true;
        }
        currentList.push(<li key={index} className="mb-1">{trimmed.substring(2)}</li>);
      } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        const boldText = trimmed.substring(2, trimmed.length - 2);
        elements.push(<p key={index} className="font-semibold mb-2">{boldText}</p>);
      } else if (trimmed) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(<p key={index} className="mb-4 text-gray-300 leading-relaxed">{trimmed}</p>);
      } else if (!trimmed && inList) {
        elements.push(<ul key={`list-${index}`} className="list-disc list-inside mb-4">{currentList}</ul>);
        currentList = [];
        inList = false;
      }
    });

    if (inList && currentList.length > 0) {
      elements.push(<ul key="final-list" className="list-disc list-inside mb-4">{currentList}</ul>);
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-dark-900 to-burgundy-900 text-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/web/policies" 
          className="inline-block mb-6 text-burgundy-400 hover:text-burgundy-300"
        >
          ← Back to Policies
        </Link>
        <div className="bg-dark-800 border border-burgundy-800 rounded-lg p-6 sm:p-8">
          {renderContent(policy.content)}
        </div>
      </div>
    </div>
  );
}

export default PolicyPage;



